/**
 * First-party error reporting. No third-party browser-error SDK in v1 — no
 * Sentry, no Bugsnag, no session replay, no fingerprinting.
 *
 * The receiving endpoint has NO DECLARED OWNER yet (PRD-TDD §23 Q9), so the
 * target is read from `VITE_FRONTEND_ERRORS_URL` and the handler is disabled
 * outright when the variable is unset: the gap degrades to "no telemetry"
 * rather than to a 404 loop.
 */
import { BUILD_SHA } from '@shared/api/config';
import { readLastRequestId } from '@shared/api/http';

import { scrub } from './scrub';

const STACK_LIMIT = 4096;

interface ErrorEvent {
  /** Splits this repo from the other two in log searches. */
  app: 'dashboard';
  route: string;
  message: string;
  stack: string | null;
  x_request_id: string | null;
  user_agent: string;
  locale: string;
  build_sha: string;
  metric_type?: 'web_vital';
}

function endpoint(): string | null {
  const url = import.meta.env.VITE_FRONTEND_ERRORS_URL?.trim();
  return url ? url : null;
}

export function isTelemetryEnabled(): boolean {
  return endpoint() !== null;
}

export function reportEvent(event: Partial<ErrorEvent> & { message: string }): void {
  const target = endpoint();
  if (!target) return;

  const payload: ErrorEvent = {
    app: 'dashboard',
    route: window.location.pathname,
    message: scrub(event.message),
    stack: event.stack ? scrub(event.stack).slice(0, STACK_LIMIT) : null,
    x_request_id: readLastRequestId(),
    user_agent: navigator.userAgent,
    locale: 'ar-YE',
    build_sha: BUILD_SHA,
    ...(event.metric_type ? { metric_type: event.metric_type } : {}),
  };

  const body = JSON.stringify(payload);

  // `sendBeacon` survives an unload; the fetch is the fallback path.
  if (typeof navigator.sendBeacon === 'function') {
    navigator.sendBeacon(target, new Blob([body], { type: 'application/json' }));
    return;
  }

  void fetch(target, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {
    /* telemetry never surfaces its own failure to the reader */
  });
}

let installed = false;

/** Global handlers for uncaught errors and unhandled rejections. */
export function installErrorReporting(): void {
  if (installed || !isTelemetryEnabled()) return;
  installed = true;

  window.addEventListener('error', (event) => {
    reportEvent({
      message: event.message,
      stack: event.error instanceof Error ? (event.error.stack ?? null) : null,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason: unknown = event.reason;
    reportEvent({
      message: reason instanceof Error ? reason.message : String(reason),
      stack: reason instanceof Error ? (reason.stack ?? null) : null,
    });
  });
}
